import React, { useEffect, useRef } from "react";
import styles from "./FullScreenPreview.module.scss";
import { CloseIcon } from "../../../assets/svg/CloseIcon";

interface FullScreenPreviewProps {
  imageUrl: string;
  onClose: () => void;
  onDownload: () => void;
}

export const FullScreenPreview: React.FC<FullScreenPreviewProps> = ({
  imageUrl,
  onClose,
  onDownload,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "=":
        case "+":
          e.preventDefault();
          setScale(prev => Math.min(prev * 1.2, 5));
          break;
        case "-":
          e.preventDefault();
          setScale(prev => Math.max(prev / 1.2, 0.1));
          break;
        case "0":
          e.preventDefault();
          setScale(1);
          setPosition({ x: 0, y: 0 });
          setRotation(0);
          break;
        case "r":
          e.preventDefault();
          setRotation(prev => prev + 90);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.1, Math.min(5, prev * delta)));
  };

  // Mouse drag
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetView = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setRotation(0);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.container}
        ref={containerRef}
        onClick={e => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.title}>Render Preview</div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            title="Close (Esc)"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Image Container */}
        <div className={styles.imageContainer}>
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Render preview"
            className={styles.image}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
              cursor: isDragging ? "grabbing" : "grab",
            }}
            draggable={false}
          />
        </div>

        {/* Footer Info */}
        <div className={styles.footer}>
          <div className={styles.info}>
            <span>Zoom: {Math.round(scale * 100)}%</span>
          </div>
          <div className={styles.hints}>
            <span>Esc to close</span>
          </div>
        </div>
      </div>
    </div>
  );
};
